import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { getUsers } from '../services/api';

function getAxiosResponseMessage(err: unknown): string | undefined {
  if (!axios.isAxiosError(err) || err.response?.data == null) return undefined;
  const data = err.response.data;
  if (typeof data === 'object' && data !== null) {
    const msg = Reflect.get(data, 'message');
    return typeof msg === 'string' ? msg : undefined;
  }
  return undefined;
}

interface ClientAnalytics {
  client_id: number;
  client_name: string;
  usage_count: number;
  last_activity: string | null;
  user_count: number;
}

interface AnalyticsData {
  clients: ClientAnalytics[];
  totalClients: number;
  totalUsers: number;
  totalAnalyses: number;
}

interface DashboardUser {
  id: number;
  role: string;
  client_id?: number | null;
  client_name?: string | null;
  number_of_listings?: number | null;
  last_activity?: string | null;
  updated_at?: string | null;
  created_at?: string | null;
}

const toNumberOrZero = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const toTime = (value: string | null | undefined): number => {
  if (!value) return 0;
  const ts = Date.parse(value);
  return Number.isNaN(ts) ? 0 : ts;
};

const buildAnalyticsFromUsers = (users: DashboardUser[]): AnalyticsData => {
  const usersWithClient = users.filter(
    user => user.role !== 'admin' && typeof user.client_id === 'number'
  );

  const grouped = new Map<number, ClientAnalytics>();
  usersWithClient.forEach(user => {
    const clientId = Number(user.client_id);
    if (!Number.isFinite(clientId)) return;
    const listingCount = Math.max(0, toNumberOrZero(user.number_of_listings));
    const activity =
      user.last_activity ||
      user.updated_at ||
      user.created_at ||
      null;
    const activityTime = toTime(activity);
    const existing = grouped.get(clientId);

    if (!existing) {
      grouped.set(clientId, {
        client_id: clientId,
        client_name: user.client_name?.trim() || `Client ${clientId}`,
        usage_count: listingCount,
        last_activity: activity,
        user_count: user.role === 'user' ? 1 : 0,
      });
      return;
    }

    existing.usage_count += listingCount;
    if (user.role === 'user') existing.user_count += 1;
    if (activityTime > toTime(existing.last_activity)) {
      existing.last_activity = activity;
    }
    if (!existing.client_name && user.client_name) {
      existing.client_name = user.client_name;
    }
  });

  const clients = Array.from(grouped.values()).sort((a, b) => b.usage_count - a.usage_count);

  return {
    clients,
    totalClients: clients.length,
    totalUsers: users.filter(user => user.role === 'user').length,
    totalAnalyses: clients.reduce((sum, c) => sum + c.usage_count, 0),
  };
};

const UsageTracking: React.FC = () => {
  const { t } = useTranslation();
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadAnalytics = async () => {
      try {
        setLoading(true);
        const users = await getUsers();
        if (cancelled) return;
        const data = buildAnalyticsFromUsers(Array.isArray(users) ? users : []);
        setAnalyticsData(data);
        setError(null);
      } catch (err) {
        console.error('Error loading analytics:', err);
        if (cancelled) return;
        const message = getAxiosResponseMessage(err);
        setError(message || t('usageTracking.errorFetching'));
        setAnalyticsData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadAnalytics();
    return () => {
      cancelled = true;
    };
  }, [t]);

  if (loading) {
    return (
      <div className="card">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card border-red-200 bg-red-50">
        <div className="text-red-700">{error}</div>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="card">
        <div className="text-gray-500">{t('usageTracking.noData')}</div>
      </div>
    );
  }

  const avgPerClient = analyticsData.totalClients > 0 
    ? Math.round(analyticsData.totalAnalyses / analyticsData.totalClients) 
    : 0;

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('usageTracking.usageAnalytics')}</h2>
      </div>

      {/* Usage Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 dark:bg-blue-900 p-4 rounded-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-8 w-8 text-blue-600 dark:text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-blue-600 dark:text-blue-300">{t('usageTracking.totalAnalyses')}</p>
              <p className="text-2xl font-semibold text-blue-900 dark:text-blue-100">
                {analyticsData.totalAnalyses}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 dark:bg-green-900 p-4 rounded-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-8 w-8 text-green-600 dark:text-green-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-600 dark:text-green-300">{t('usageTracking.clientsUsers')}</p>
              <p className="text-2xl font-semibold text-green-900 dark:text-green-100">
                {analyticsData.totalClients} / {analyticsData.totalUsers}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-purple-50 dark:bg-purple-900 p-4 rounded-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-8 w-8 text-purple-600 dark:text-purple-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-purple-600 dark:text-purple-300">{t('usageTracking.avgPerClient')}</p>
              <p className="text-2xl font-semibold text-purple-900 dark:text-purple-100">
                {avgPerClient}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('usageTracking.client')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('usageTracking.usageCount')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('usageTracking.lastActivity')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('usageTracking.userCount')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {analyticsData.clients.map((client) => (
                <tr key={client.client_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
                          <span className="text-white font-medium text-sm">
                            {client.client_name?.charAt(0)?.toUpperCase() || 'C'}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{client.client_name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    <div className="flex items-center">
                      <span className="font-medium">{client.usage_count}</span>
                      <div className="ml-2 w-16 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div 
                          className="bg-blue-600 dark:bg-blue-400 h-2 rounded-full" 
                          style={{ 
                            width: `${Math.min((client.usage_count / Math.max(...analyticsData.clients.map(c => c.usage_count))) * 100, 100)}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {client.last_activity 
                      ? new Date(client.last_activity).toLocaleDateString()
                      : t('usageTracking.noActivity')
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    <span className="font-medium">{client.user_count}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UsageTracking;